<script setup>
import { VDataTableServer } from "vuetify/labs/VDataTable";
import moment from "moment-timezone";

moment.tz.setDefault("Asia/Jakarta");

const loading = ref();
const loadingTable = ref(false);
const loadingClear = ref();

definePage({
  meta: {
    layout: "default",
  },
});
const pageTitle = "Data Shop";

const breadCrumbs = [
  {
    title: "Dashboard",
    to: "/admin/dashboard",
  },
  {
    title: "Master Data",
    to: null,
  },
  {
    title: pageTitle,
    to: null,
  },
];
const headers = [
  {
    title: "No",
    sortable: false,
    key: "id",
  },
  {
    title: "Type",
    key: "type",
  },
  {
    title: "Shop Name",
    key: "name",
  },
  {
    title: "Phone",
    key: "phoneNumber",
  },
  {
    title: "Email",
    key: "email",
  },
  {
    title: "The number of location",
    key: "location",
  },
  {
    title: "Last Update",
    key: "updatedAt",
  },
  {
    title: "Actions",
    sortable: false,
    key: "actions",
  },
];

const route = useRoute();
const router = useRouter();

const refForm = ref();

const valid = ref("");
const errors = ref("");

// Data table options
const itemsPerPage = ref(10);
const page = ref(1);
const orderBy = ref("createdAt");
const orderDirection = ref("ASC");

const searchByMerchantId = ref(null);
const searchByMerchantIdParse = ref(null);

const searchByShopId = ref(null);
const searchByName = ref(null);
const searchByPhoneNumber = ref(null);

const { data: dataQueries, execute: fetchQueries } = await useApi(
  createUrl("/cms/shop", {
    query: {
      limit: itemsPerPage,
      searchByMerchantId: searchByMerchantIdParse,
      searchByShopId: searchByShopId,
      searchByName: searchByName,
      searchByPhoneNumber: searchByPhoneNumber,
      page,
      orderBy,
      orderDirection,
    },
  })
);

const data = dataQueries
  ? dataQueries.value
    ? dataQueries.value?.data
    : []
  : [];

const updateOptions = async (options) => {
  loadingTable.value = true;
  page.value = options.page;
  orderBy.value = options.sortBy[0]?.key;
  orderDirection.value = options.sortBy[0]?.order;
  fetchQueries().then((value) => {
    loadingTable.value = false;
  });
};

const perPages = [5, 10, 25, 50];

const refreshResult = () => {
  loadingClear.value = true;
  searchByMerchantId.value = null;
  searchByMerchantIdParse.value = null;
  searchByShopId.value = null;
  searchByName.value = null;
  searchByPhoneNumber.value = null;
  fetchQueries().then((value) => {
    loadingClear.value = false;
  });
};

const searchResult = () => {
  loading.value = true;
  loadingTable.value = true;
  if (searchByMerchantId.value) {
    searchByMerchantIdParse.value = searchByMerchantId.value.split(" : ", 1)[0];
  } else {
    searchByMerchantIdParse.value = "";
  }
  refForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) {
      fetchQueries().then((value) => {
        loading.value = false;
        loadingTable.value = false;
      });
    }
  });
};

const dataItems = computed(() => (dataQueries ? dataQueries.value?.data : []));
const totalItems = computed(() =>
  dataQueries ? dataQueries.value?.metadata.pagination.itemCount : []
);

/* Merchant */
const referMerchant = ref(false);
const referMerchantClick = (item) => {
  referMerchant.value = item;
};

/*
 * Delete
 * */
const deleteDialog = ref(false);
const deleteAlert = ref(false);
const deleteItem = (e) => {
  deleteDialog.value = e;
};
const deleteOk = async (e) => {
  loadingTable.value = true;
  try {
    const res = await $api("/cms/shop/" + e, {
      method: "DELETE",
      onResponseError({ response }) {
        errors.value = response._data.message;

        setTimeout(() => (errors.value = false), 10000);
      },
    });

    if (res.statusCode === 200) {
      deleteAlert.value = res.message;
    }
  } catch (err) {
    deleteAlert.value = `The record count not be deleted, because of an association`;
    errors.value = err.message;
  }
  fetchQueries().then((value) => {
    loadingTable.value = false;
    deleteAlert.value = false;
    errors.value = null;
    deleteDialog.value = false;
    deleteDialogDisplay.value = "";
  });
};
const deleteCancel = () => {
  deleteDialog.value = false;
};
</script>

<template>
  <VBreadcrumbs :items="breadCrumbs" class="px-0 pt-0 pb-5" />
  <h4 class="text-h4 my-4">{{ pageTitle }}</h4>
  <VAlert v-show="valid" type="success">
    {{ valid }}
  </VAlert>
  <VAlert v-show="errors" type="error">
    {{ errors }}
  </VAlert>
  <VCard>
    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :headers="headers"
      :items="dataItems"
      :items-length="totalItems"
      :loading="loadingTable"
      class="text-no-wrap"
      @update:options="updateOptions"
    >
      <template #top>
        <div>
          <VForm
            ref="refForm"
            @submit.prevent="
              () => {
                searchResult();
              }
            "
          >
            <VRow
              class="ms-4 me-4 my-2 justify-center justify-sm-space-between"
            >
              <VCol cols="12" lg="6">
                <VRow class="align-center">
                  <VCol cols="10">
                    <AppTextField
                      v-model="searchByMerchantId"
                      label="Merchant id"
                      readonly
                    />
                  </VCol>
                  <VCol class="pt-9" cols="2">
                    <VBtn
                      block
                      color="primary"
                      type="button"
                      @click="referMerchantClick(true)"
                      >Refer
                    </VBtn>
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12" lg="6">
                <AppTextField
                  v-model="searchByShopId"
                  label="Shop ID"
                  placeholder="Input Shop Id"
                />
              </VCol>
              <VCol cols="12" lg="6">
                <AppTextField
                  v-model="searchByName"
                  label="Shop Name"
                  placeholder="Input Shop Name"
                />
              </VCol>
              <VCol cols="12" lg="6">
                <AppTextField
                  v-model="searchByPhoneNumber"
                  label="Phone"
                  placeholder="Input Phone"
                />
              </VCol>
            </VRow>
            <div>
              <VCardText
                class="d-flex align-center flex-wrap justify-space-between gap-4"
              >
                <VSpacer />
                <VBtn
                  :disabled="loading"
                  :loading="loading"
                  color="primary"
                  type="submit"
                >
                  Search

                  <template #loader>
                    <span class="custom-loader">
                      <VIcon class="text-white" icon="tabler-refresh" />
                    </span>
                  </template>
                </VBtn>
                <VBtn
                  :disabled="loadingClear"
                  :loading="loadingClear"
                  color="secondary"
                  type="reset"
                  variant="tonal"
                  @click="refreshResult()"
                >
                  <VIcon icon="tabler-refresh" start />
                  Clear
                </VBtn>
                <VSpacer />
              </VCardText>
            </div>
          </VForm>
        </div>

        <div>
          <VRow class="ms-4 me-4 my-2">
            <VCol cols="12" lg="2" sm="2">
              <AppSelect
                v-model="itemsPerPage"
                :items="perPages"
                class="ml-auto"
                placeholder="Per Page"
              />
            </VCol>
            <VCol cols="12" lg="2" sm="2">
              <VBtn :to="{ path: 'shop/create' }" block color="primary">
                <VIcon icon="tabler-plus" start />
                Create {{ pageTitle }}
              </VBtn>
            </VCol>
          </VRow>
        </div>
      </template>
      <template #item.updatedAt="{ item }"
        >{{ moment(item.updatedAt).format("LLL") }}
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex gap-1">
          <IconBtn :to="{ path: 'shop/view/' + item.id }">
            <VIcon icon="tabler-edit" />
          </IconBtn>
          <IconBtn @click="deleteItem(item.id)">
            <VIcon icon="tabler-trash" />
          </IconBtn>
        </div>
      </template>
      <template #bottom>
        <VCardText class="pt-2">
          <div
            class="d-flex flex-wrap justify-center justify-sm-space-between gap-y-2 mt-2"
          >
            <div></div>
            <VPagination
              v-model="page"
              :length="Math.ceil(totalItems / itemsPerPage)"
              :total-visible="$vuetify.display.smAndDown ? 3 : 5"
            />
          </div>
        </VCardText>
      </template>
    </VDataTableServer>
  </VCard>
  <!-- Dialog -->
  <VDialog v-model="referMerchant" max-width="500px">
    <DialogCloseBtn @click="referMerchantClick(false)" />
    <VCard class="pa-5 pa-sm-8">
      <MerchantRefer
        grab="id"
        @set-merchant-refer="(e) => (searchByMerchantId = e)"
      />
    </VCard>
  </VDialog>
  <!-- 👉 Delete Dialog  -->
  <VDialog v-model="deleteDialog" max-width="500px">
    <VCard v-show="!deleteAlert">
      <VCardTitle class="text-center my-3">
        <p>Are you sure you want to delete this item?</p>
        <h3>ID : {{ deleteDialog }}</h3>
      </VCardTitle>

      <VCardActions>
        <VSpacer />
        <VBtn color="secondary" variant="outlined" @click="deleteCancel">
          Cancel
        </VBtn>
        <VBtn color="error" variant="elevated" @click="deleteOk(deleteDialog)">
          OK
        </VBtn>
        <VSpacer />
      </VCardActions>
    </VCard>
    <VCard v-show="deleteAlert">
      <VCardText>
        <VAlert type="error">
          {{ deleteAlert }}
        </VAlert>
      </VCardText>
    </VCard>
  </VDialog>
</template>
